import { QuoteHistoryItem, RateResult } from "../types";
import { downloadCsv, downloadCsvByDeliveredType } from "../csv";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";

type QuoteHistoryTableProps = {
  history: QuoteHistoryItem[];
  error?: string;
};

function getDeliveredTypeLabel(type: string) {
  if (type === "D") return "Domicilio";
  if (type === "S") return "Sucursal";
  if (type === "ALL") return "Dom. + Suc.";
  return type || "-";
}

// Las fechas vienen del servidor en formato ISO. Se muestran en horario local
// y se tolera el formato viejo, por si quedó algo cargado en la sesión.
//
// Formato corto (28/08, 21:14) a propósito: la tabla vive en una columna
// angosta y el año y los segundos no aportan nada para reconocer una fila.
function formatFecha(valor: string) {
  const fecha = new Date(valor);

  return Number.isNaN(fecha.getTime())
    ? valor
    : fecha.toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
}

// `validTo` es el campo que devuelve Correo. Medido contra la API en cuatro
// cotizaciones reales, viene igual al instante de la respuesta —medio segundo
// antes— y no a un vencimiento futuro. Por eso se muestra como dato y no se
// afirma vigencia: si se comparara contra el reloj, toda cotización aparecería
// vencida apenas se crea.
//
// Cuánto tiempo se respeta un precio cotizado es, entonces, una decisión de la
// empresa, todavía sin definir.
//
// Al ser prácticamente igual a la fecha de la fila, ya no ocupa una columna
// propia: viaja en el tooltip de la fecha (y entero en el CSV).
function describirHoraCorreo(item: QuoteHistoryItem) {
  if (!item.validTo) return "Correo no devolvió el campo validTo.";

  const hasta = new Date(item.validTo);

  if (Number.isNaN(hasta.getTime())) return "Correo devolvió un validTo ilegible.";

  return `Correo respondió a las ${hasta.toLocaleTimeString(
    "es-AR"
  )} (campo validTo: es el instante de la respuesta, no un vencimiento).`;
}

// Un error de configuración del servidor no le sirve al vendedor (y expone
// nombres internos): se muestra un mensaje neutro. Los errores de validación
// (peso, medidas) sí se muestran tal cual, porque son accionables.
// Red de último recurso para las filas viejas. Desde el arreglo de la
// auditoría el servidor ya no manda mensajes internos —los traduce antes de
// guardarlos y de responder—, pero el historial conserva lo que se guardó
// ANTES, y esas filas siguen teniendo el texto crudo.
//
// El filtro mira los nombres de variable de entorno (con guión bajo), no la
// marca: "MiCorreo" a secas aparece en los mensajes buenos ("No se pudo
// conectar con MiCorreo"), y taparlos escondía la única explicación útil.
function formatError(mensaje: string) {
  return /variables de entorno|MICORREO_|PAQAR_|MP_[A-Z]/.test(mensaje)
    ? "No se pudo cotizar: falla del sistema (avisar al administrador)."
    : mensaje;
}

function formatPrice(price: number | string) {
  if (typeof price === "number") return `$ ${price.toLocaleString("es-AR")}`;

  const numero = Number(price);

  return Number.isFinite(numero) && String(price).trim() !== ""
    ? `$ ${numero.toLocaleString("es-AR")}`
    : String(price);
}

// "Correo Argentino Clasico" → "Clasico": el prestador es siempre el mismo y
// repetirlo en cada renglón se come el ancho de la columna.
function nombreCorto(servicio: string) {
  return servicio.replace(/^correo argentino\s*/i, "") || servicio;
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  color: "var(--text-secondary)",
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid var(--border-color)",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  padding: "10px 10px",
  color: "var(--text-primary)",
  fontSize: 13,
  borderBottom: "1px solid var(--border-color)",
  verticalAlign: "top",
};

// Datos que no se parten al medio (una fecha o un par de codigos postales
// cortados en dos renglones no se leen).
const tdFirme: React.CSSProperties = { ...td, whiteSpace: "nowrap" };

// Segunda línea de una celda: el dato de contexto que antes era su propia
// columna (modo, tipo de entrega, peso facturable).
const sub: React.CSSProperties = {
  color: "var(--text-secondary)",
  fontSize: 11,
  marginTop: 3,
};

const botonChico: React.CSSProperties = {
  minHeight: 0,
  padding: "8px 12px",
  fontSize: 12,
  borderRadius: 10,
};

function ResultadoLinea({ result }: { result: RateResult }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        flexWrap: "wrap",
        gap: "0 8px",
      }}
    >
      <span style={{ ...sub, marginTop: 0, minWidth: 62 }}>
        {getDeliveredTypeLabel(result.deliveredType)}
      </span>

      <span style={{ fontWeight: 700 }}>{formatPrice(result.price)}</span>

      <span style={{ ...sub, marginTop: 0 }}>
        {nombreCorto(result.service)}
        {result.sla ? ` · ${result.sla}` : ""}
      </span>
    </div>
  );
}

export function QuoteHistoryTable({
  history,
  error,
}: QuoteHistoryTableProps) {
  return (
    <Card>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 18,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "var(--text-primary)", fontSize: 20 }}>
            Historial de cotizaciones
          </h2>

          <p
            style={{
              margin: "6px 0 0",
              color: "var(--text-secondary)",
              fontSize: 13,
            }}
          >
            Exportá el análisis completo o separado por tipo de entrega.
          </p>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => downloadCsv(history)}
            disabled={history.length === 0}
            style={botonChico}
          >
            CSV completo
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              downloadCsvByDeliveredType(history, "D", "lynxpack-domicilio")
            }
            disabled={history.length === 0}
            style={botonChico}
          >
            CSV Domicilio
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              downloadCsvByDeliveredType(history, "S", "lynxpack-sucursal")
            }
            disabled={history.length === 0}
            style={botonChico}
          >
            CSV Sucursal
          </Button>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "51%" }} />
          </colgroup>

          <thead>
            <tr>
              <th style={th}>Fecha</th>
              <th style={th}>Ruta</th>
              <th style={th}>Bulto</th>
              <th style={th}>Resultados</th>
            </tr>
          </thead>

          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ ...td, textAlign: "center", padding: 28 }}>
                  {error || "Sin cotizaciones todavía."}
                </td>
              </tr>
            ) : (
              history.map((item) => (
                <tr key={item.id}>
                  <td style={tdFirme} title={describirHoraCorreo(item)}>
                    {formatFecha(item.createdAt)}

                    <div style={sub}>
                      {item.mode === "batch" ? "Lote" : "Manual"}
                    </div>
                  </td>

                  <td style={tdFirme}>
                    {item.postalCodeOrigin} → {item.postalCodeDestination}

                    <div style={sub}>
                      {getDeliveredTypeLabel(item.deliveredType)}
                    </div>
                  </td>

                  <td style={td}>
                    {item.height}x{item.width}x{item.length} cm

                    <div
                      style={sub}
                      title={
                        item.billableKg !== undefined
                          ? `Peso real ${item.weight} g · peso facturable ${item.billableKg} kg (el que define el precio)`
                          : `Peso real ${item.weight} g`
                      }
                    >
                      {item.weight} g
                      {item.billableKg !== undefined
                        ? ` · ${item.billableKg} kg`
                        : ""}
                    </div>
                  </td>

                  <td style={{ ...td, whiteSpace: "normal" }}>
                    {item.error ? (
                      <span style={{ color: "var(--danger)" }}>
                        {formatError(item.error)}
                      </span>
                    ) : item.results.length === 0 ? (
                      <span style={{ color: "var(--text-secondary)" }}>
                        Correo no ofrece servicio para ese bulto hacia ese
                        destino.
                      </span>
                    ) : (
                      <div style={{ display: "grid", gap: 4 }}>
                        {item.results.map((result, index) => (
                          <ResultadoLinea
                            key={`${result.deliveredType}-${result.service}-${index}`}
                            result={result}
                          />
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
